import clsx from 'clsx';
import type React from 'react';
import { useEffect, useId, useRef, useState } from 'react';
// Asset imports - bundler handles path resolution
import closeIcon from '~/static/assets/icons/landing-page/close-icon.svg';

import { useLaunchMeeting } from '../../hooks/useLaunchMeeting';
import { usePerformanceTracking } from '../../services/performanceMonitor';
import { PASSCODE_ALLOWED } from '../PersonalMeetingsModal/helpers/constants';
import { validateMeetingIdFormat } from '../PersonalMeetingsModal/helpers/validators';

interface JoinMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const JoinMeetingModal: React.FC<JoinMeetingModalProps> = ({ isOpen, onClose }) => {
  const { trackInteraction } = usePerformanceTracking('Join Meeting');
  const launchMeeting = useLaunchMeeting();
  const [meetingId, setMeetingId] = useState('');
  const [meetingIdError, setMeetingIdError] = useState<string | undefined>();
  const [passcode, setPasscode] = useState('');
  const [passcodeError, setPasscodeError] = useState<string | undefined>();
  const [showPasscode, setShowPasscode] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const meetingIdInputRef = useRef<HTMLInputElement>(null);

  // Generate unique IDs for accessibility
  const uniqueId = useId();
  const modalTitleId = `${uniqueId}-modal-title`;
  const meetingIdInputId = `${uniqueId}-meeting-id`;
  const meetingIdDescId = `${uniqueId}-meeting-id-desc`;
  const passcodeInputId = `${uniqueId}-passcode`;
  const passcodeDescId = `${uniqueId}-passcode-desc`;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setMeetingId('');
      setMeetingIdError(undefined);
      setPasscode('');
      setPasscodeError(undefined);
      meetingIdInputRef.current?.focus();
    }
  }, [isOpen]);

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle click outside modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const endTracking = trackInteraction('join_meeting_submit');
    if (meetingId.trim() && !passcodeError) {
      onClose();
      launchMeeting(meetingId.trim(), passcode);
    }
    endTracking();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleMeetingIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMeetingId(value);
    const { isValid, error } = validateMeetingIdFormat(value);
    setMeetingIdError(isValid ? undefined : error);
  };

  const handlePasscodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPasscode(value);
    if (value && !PASSCODE_ALLOWED.test(value)) {
      setPasscodeError('Passcode must contain only digits');
    } else {
      setPasscodeError(undefined);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby={modalTitleId}>
      <div ref={modalRef} className="join-meeting-modal">
        {/* Modal Header */}
        <div className="modal-header">
          <h2 id={modalTitleId} className="modal-title">
            Join a meeting
          </h2>
          <button
            type="button"
            className="modal-close-button"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <img
              src={closeIcon.src}
              width={closeIcon.width}
              height={closeIcon.height}
              alt=""
              className="close-icon"
              aria-hidden="true"
            />
          </button>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="modal-content">
          {/* Meeting ID Field */}
          <div className="form-group">
            <label htmlFor={meetingIdInputId} className="form-label">
              Meeting ID
              <span className="info-icon-wrapper">
                <svg
                  className="info-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                  <text
                    x="8"
                    y="11.5"
                    textAnchor="middle"
                    fill="currentColor"
                    fontSize="10"
                    fontWeight="600"
                    fontFamily="Inter, sans-serif"
                  >
                    i
                  </text>
                </svg>
                <span className="tooltip">Supported characters: 0-9, a-z, A-Z, -, _</span>
              </span>
            </label>
            <input
              ref={meetingIdInputRef}
              type="text"
              id={meetingIdInputId}
              className={clsx('form-input', { 'form-input--error': meetingIdError })}
              value={meetingId}
              onChange={handleMeetingIdChange}
              placeholder="Meeting ID"
              required
              aria-describedby={meetingIdError ? `${meetingIdInputId}-error` : meetingIdDescId}
              aria-invalid={!!meetingIdError}
            />
            {meetingIdError ? (
              <div id={`${meetingIdInputId}-error`} className="form-error" role="alert">
                {meetingIdError}
              </div>
            ) : (
              <div id={meetingIdDescId} className="sr-only">
                Enter the meeting ID provided by the meeting organizer
              </div>
            )}
          </div>

          {/* Passcode Field */}
          <div className="form-group">
            <label htmlFor={passcodeInputId} className="form-label">
              Passcode
              <span className="info-icon-wrapper">
                <svg
                  className="info-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                  <text
                    x="8"
                    y="11.5"
                    textAnchor="middle"
                    fill="currentColor"
                    fontSize="10"
                    fontWeight="600"
                    fontFamily="Inter, sans-serif"
                  >
                    i
                  </text>
                </svg>
                <span className="tooltip">Supported characters: 0-9</span>
              </span>
            </label>
            <div className="input-with-toggle">
              <input
                type={showPasscode ? 'text' : 'password'}
                id={passcodeInputId}
                className={clsx('form-input', { 'form-input--error': passcodeError })}
                value={passcode}
                onChange={handlePasscodeChange}
                placeholder="Passcode"
                aria-describedby={passcodeError ? `${passcodeInputId}-error` : passcodeDescId}
                aria-invalid={!!passcodeError}
              />
              <button
                type="button"
                className="passcode-toggle-button"
                onClick={() => setShowPasscode(!showPasscode)}
                aria-label={showPasscode ? 'Hide passcode' : 'Show passcode'}
              >
                <img
                  src={
                    showPasscode
                      ? 'assets/icons/landing-page/eye-off-icon.svg'
                      : 'assets/icons/landing-page/eye-icon.svg'
                  }
                  alt=""
                  aria-hidden="true"
                  width={20}
                  height={20}
                />
              </button>
            </div>
            {passcodeError ? (
              <div id={`${passcodeInputId}-error`} className="form-error" role="alert">
                {passcodeError}
              </div>
            ) : (
              <div id={passcodeDescId} className="sr-only">
                Enter the passcode if required by the meeting (optional)
              </div>
            )}
          </div>

          {/* Modal Actions */}
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={handleCancel}>
              CANCEL
            </button>
            <button
              type="submit"
              className="join-button"
              disabled={!!meetingIdError || !!passcodeError || !meetingId.trim()}
            >
              JOIN
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { JoinMeetingModal };
